import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Panel, PanelGroup, designSystem } from '@yola/ws-ui';
import CustomHtmlRenderOption from './custom-html-render-option';
import fieldNames from '../constants/code-snippet-field-names';

const {
  DialogHeader,
  DialogHeaderTitle,
  ControlGroup,
  InputGroupField,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  Heading,
  HorizontalStack,
  SimpleSelectField,
  CodeEditorField,
  CtaBanner,
  ActionButton,
} = designSystem;

const MODAL_WIDTH = 800;
const MODAL_HEIGHT = 720;
const MENU_WIDTH = '160px';

const CodeSnippetDialog = ({
  isDesktopView,
  isNewSnippet = false,
  isAvailable,
  captions,
  control,
  renderOptions,
  codeEditorErrorMessage = null,
  onSubmit,
  onCancel,
  onDelete,
  onUpgrade,
  onCodeEditorFocus,
  onCodeEditorError,
}) => {
  const [bannerKey, setBannerKey] = useState(0);

  const codeEditorRules = useMemo(
    () => ({
      required: captions.required,
      validate: () => codeEditorErrorMessage.current || null,
    }),
    [codeEditorErrorMessage, captions.required]
  );

  const reRenderBanner = useCallback(() => {
    if (!isAvailable) {
      setBannerKey((prevKey) => prevKey + 1);
    }
  }, [isAvailable]);

  const handleSubmit = useCallback(() => {
    reRenderBanner();
    onSubmit();
  }, [onSubmit, reRenderBanner]);

  return (
    <Modal
      centered
      width={MODAL_WIDTH}
      minHeight={MODAL_HEIGHT}
      overlay="visible"
      draggable={false}
      resizable={false}
      fullscreen={!isDesktopView}
      handleCancel={onCancel}
      handleSubmit={handleSubmit}
    >
      <PanelGroup height="100%">
        <Panel align="middle" corners="squared" className="ws-code-snippet-dialog__header">
          <DialogHeader>
            <DialogHeaderTitle>{captions.title}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <Panel className="ws-code-snippet-dialog__content-wrapper" height="100%" scrollable>
          <div className="ws-code-snippet-dialog__content">
            <ControlGroup title={captions.fields.name}>
              <InputGroupField
                disabled={!isAvailable}
                name={fieldNames.NAME}
                control={control}
                rules={{ required: captions.required }}
              />
            </ControlGroup>
            <div className="ws-code-snippet-dialog__field-group">
              <HorizontalStack align="space-between" verticalAlign="center">
                <Heading type="heading-6">{captions.fields.code}</Heading>
                {isDesktopView && (
                  <HorizontalStack verticalAlign="center">
                    <Heading type="heading-6">{captions.fields.rendering}</Heading>
                    <SimpleSelectField
                      name={fieldNames.RENDERING}
                      control={control}
                      options={renderOptions}
                      transparent
                      preventOverflow
                      noVerticalSpacing
                      noHorizontalSpacing
                      menuWidth={MENU_WIDTH}
                      menuAlignment="right"
                      components={{ Option: CustomHtmlRenderOption }}
                    />
                  </HorizontalStack>
                )}
              </HorizontalStack>
              <div className="ws-code-snippet-dialog__code-field" onClick={reRenderBanner}>
                <CodeEditorField
                  height="stretch"
                  name={fieldNames.CODE}
                  control={control}
                  rules={codeEditorRules}
                  disabled={!isAvailable}
                  options={{
                    readOnly: !isAvailable,
                    showPrintMargin: true,
                    showLineNumbers: true,
                    tabSize: 2,
                    autoScrollEditorIntoView: true,
                  }}
                  onFocus={onCodeEditorFocus}
                  onEditorError={onCodeEditorError}
                />
              </div>
              {!isAvailable && (
                <CtaBanner
                  key={bannerKey}
                  title={captions.upgradeBanner.description}
                  iconGlyph="upgrade"
                  animate
                  ctaButton={{
                    label: captions.upgradeBanner.upgrade,
                    onClick: onUpgrade,
                  }}
                />
              )}
            </div>
            {!isNewSnippet && (
              <div>
                <ActionButton
                  appearance="danger"
                  label={captions.buttons.delete}
                  onClick={onDelete}
                />
              </div>
            )}
          </div>
        </Panel>
        <Panel corners="squared" className="ws-code-snippet-dialog__footer">
          <DialogButtonGroup>
            <DialogButton onClick={handleSubmit}>{captions.buttons.submit}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.buttons.cancel}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

CodeSnippetDialog.propTypes = {
  isDesktopView: PropTypes.bool.isRequired,
  isNewSnippet: PropTypes.bool,
  isAvailable: PropTypes.bool.isRequired,
  captions: PropTypes.shape({
    title: PropTypes.string.isRequired,
    buttons: PropTypes.shape({
      submit: PropTypes.string.isRequired,
      cancel: PropTypes.string.isRequired,
      delete: PropTypes.string,
    }).isRequired,
    fields: PropTypes.shape({
      name: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      rendering: PropTypes.string.isRequired,
    }).isRequired,
    upgradeBanner: PropTypes.shape({
      description: PropTypes.string.isRequired,
      upgrade: PropTypes.string.isRequired,
    }).isRequired,
    required: PropTypes.string.isRequired,
  }).isRequired,
  control: PropTypes.shape({}).isRequired,
  renderOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  codeEditorErrorMessage: PropTypes.shape({ current: PropTypes.string }),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCodeEditorFocus: PropTypes.func.isRequired,
  onCodeEditorError: PropTypes.func.isRequired,
};

export default CodeSnippetDialog;
